/**
 * Firestore access for `skidaCrmEmailSuppressions` — read-only, on purpose.
 *
 * The collection is written by the Resend webhook when a message bounces
 * permanently or somebody marks one as spam, and by the unsubscribe page when
 * the reader has no skida.app account to record the choice on — a cold lead, an
 * ambassador, a partner. `resolveAudience` subtracts it from every audience
 * before a send. The rules refuse a client write outright
 * (`allow write: if false`), and this repository has no create, update or delete
 * to match: removing an entry means mailing somebody who told us to stop.
 *
 * Small — hundreds of rows against audiences in the tens of thousands — so it is
 * read whole.
 */
import {
  collection,
  onSnapshot,
  query,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { toIsoOrNull } from '@/shared/util/dates'

const COLLECTION = 'skidaCrmEmailSuppressions'

export type SuppressionReason = 'bounced' | 'complained' | 'unsubscribed'

export interface Suppression {
  /** The document id is the address. */
  email: string
  reason: SuppressionReason
  /** The provider's own words: "mailbox_does_not_exist", "Marked as spam". */
  detail: string
  /** The send it happened on, when the webhook knew one. */
  sendId: string | null
  at: string | null
}

const str = (v: unknown) => (typeof v === 'string' ? v : '')

function map(d: QueryDocumentSnapshot<DocumentData>): Suppression {
  const v = d.data()
  const reason = str(v.reason)
  return {
    email: str(v.email) || d.id,
    reason:
      reason === 'complained' ? 'complained' : reason === 'unsubscribed' ? 'unsubscribed' : 'bounced',
    detail: str(v.detail),
    sendId: str(v.sendId) || null,
    at: toIsoOrNull(v.at),
  }
}

export function subscribeToSuppressions(
  onData: (rows: Suppression[]) => void,
  onError: (e: Error) => void,
): () => void {
  return onSnapshot(
    // No `orderBy('at')`: an entry written before the field existed would be
    // dropped from the list rather than sorted last, and this is precisely the
    // list where a missing row is invisible. Sorted here instead.
    query(collection(db, COLLECTION)),
    (snap) => {
      const rows = snap.docs.map(map)
      rows.sort((a, b) => (b.at ?? '').localeCompare(a.at ?? '') || a.email.localeCompare(b.email))
      onData(rows)
    },
    (e) => onError(e as Error),
  )
}
